<template>
    <div class="job-task loading-container">
        <loading :active="loading" :is-full-page="false" />
        <div class="task-body">
            <JobLink :executiveId="item.primaryEntityId" :companyId="providerDto.companyId" v-if="providerDto" />

            <h2>Job {{ action }} - {{ company.name }}</h2>

            <dl>
                <dt>Confirmed Date</dt>
                <dd>{{ jobHistoryConfirmedDate }}</dd>
            </dl>

            <form @submit.prevent v-if="updated">
                <mt-input
                    type="date"
                    v-model="updated.startDate"
                    label="Start Date"
                    :helpText="existing.startDate"
                    :disabled="disabled"
                />
                <mt-input
                    type="date"
                    v-model="updated.endDate"
                    label="End Date"
                    :helpText="existing.endDate"
                    :disabled="disabled"
                />
                <mt-checkbox v-model="updated.active" label="Active" :helpText="existing.active" :disabled="disabled" />
            </form>

            <InputError :errorMessage="errors" />
        </div>
        <div class="task-actions">
            <button @click="ignore" class="danger">Ignore</button>
            <button @click="updateJob" class="primary" v-if="action === 'updated'">Update</button>
            <button @click="insertJob" class="primary" v-if="action === 'discovered'">Import</button>
            <button @click="deleteJob" class="primary" v-if="action === 'deleted'">Delete</button>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request'
import JobLink from './JobLink'
import TaskService from '@/services/manage/taskService'
import InputError from '@/components/shared/forms/input-error'
import CompanyService from '@/services/manage/companyService'
import ExecutiveService from '@/services/manage/executiveService'
import { ProviderJobService } from '@/services/manage/ProviderJobService'

export default {
    components: { InputError, JobLink },
    props: {
        item: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            errors: [],
            saving: false,
            updated: null,
        }
    },
    watch: {
        providerDto() {
            const providerDto = this.providerDto

            if (!this.providerDto) {
                this.updated = null
            } else {
                this.updated = {
                    executiveId: providerDto.executiveId,
                    companyId: providerDto.companyId,
                    startDate: providerDto.startDate?.substring(0, 10),
                    endDate: providerDto.endDate?.substring(0, 10),
                    active: providerDto.active,
                }
            }

            this.errors = []
        },
    },
    computed: {
        action() {
            if (this.providerDto?.tombstoned) {
                return 'deleted'
            }

            if (this.existing?.id) {
                return 'updated'
            }

            return 'discovered'
        },
        disabled() {
            return this.action === 'deleted'
        },
        executiveId() {
            return this.item.primaryEntityId
        },
        id() {
            return this.item.secondaryEntityId
        },
        loading() {
            return (
                this.$asyncComputed.existing.updating ||
                this.$asyncComputed.jobHistoryConfirmedDate.updating ||
                this.$asyncComputed.providerDto.updating ||
                this.saving
            )
        },
    },
    asyncComputed: {
        company: {
            get() {
                if (this.providerDto) {
                    return CompanyService.get(this.providerDto.companyId)
                }
                return {}
            },
            default: {},
        },
        existing: {
            async get() {
                if (this.providerDto?.jobId) {
                    const job = await request.get(`/api/manage/jobs/${this.providerDto.jobId}`)

                    if (job) {
                        return {
                            ...job,
                            startDate: job.startDate?.substring(0, 10),
                            endDate: job.endDate?.substring(0, 10),
                        }
                    }
                }

                return {}
            },
            default() {
                return {}
            },
        },
        jobHistoryConfirmedDate: {
            async get() {
                return await ExecutiveService.getJobHistoryConfirmedDate(this.executiveId)
            },
            default: null,
        },
        providerDto: {
            get() {
                return request.get(`/api/manage/provider-jobs/${this.id}`)
            },
            default: null,
        },
    },
    methods: {
        async deleteJob() {
            this.save(async () => {
                const { ok } = await request.delete(`/api/manage/jobs/${this.providerDto.jobId}`)
                if (ok) {
                    TaskService.complete(this.item)
                    this.$bus.publish('jobs-updated', this.providerDto.executiveId)
                } else {
                    this.$flash('Error deleting job.', 'error')
                }
            })
        },
        async insertJob() {
            this.save(async () => {
                const { ok, status, content } = await request.post(
                    `/api/manage/provider-jobs/${this.id}/import`,
                    this.updated
                )

                if (ok) {
                    this.$bus.publish('task-completed', this.item)
                    this.$bus.publish('jobs-updated', this.providerDto.executiveId)
                    return
                }

                if (status === 422) {
                    this.$flash('Error importing job.', 'error')
                    this.errors = Object.values(content).flat()
                    return
                }

                this.$flash('Unknown error importing job.', 'error')
            })
        },
        async ignore() {
            this.save(async () => {
                if (this.action === 'deleted') {
                    await TaskService.complete(this.item)
                    return
                }

                const { ok } = await ProviderJobService.ignore(this.id)

                if (ok) {
                    this.$flash('Successfully ignored job.', 'success')
                    this.$bus.publish('task-completed', this.item)
                } else {
                    this.$flash('Error ignoring job.', 'error')
                }
            })
        },
        async updateJob() {
            this.save(async () => {
                const { ok, status, content } = await request.put(`/api/manage/jobs/${this.existing.id}`, {
                    ...this.updated,
                    id: this.existing.id,
                })

                if (ok) {
                    await TaskService.complete(this.item)
                    this.$bus.publish('jobs-updated', this.providerDto.executiveId)
                    return
                }

                if (status === 422) {
                    this.$flash('Error importing job.', 'error')
                    this.errors = Object.values(content).flat()
                    return
                }

                this.$flash('Unknown error importing job.', 'error')
            })
        },
        async save(callback) {
            this.saving = true
            this.errors = []
            await callback()
            this.saving = false
        },
    },
}
</script>
